import {
  Box,
  LinearProgress,
  styled,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useGenerateReportsReportsGetQuery } from "api";
import { ZoneBreadcrumbs } from "components/ZoneBreadcrumbs/ZoneBreadcrumbs";
import { hasDeviationAlert } from "interfaces/nightReport.interface";

import { NightReportAccordion } from "./NightReportAccordion";
import NightReportTable from "./NightReportTable";

import { useMonitoringStore } from "../../monitoringStore";

const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  borderColor: theme.vars.palette.borders.neutral.subtle,
  borderRadius: theme.spacing(1),
  borderStyle: "solid",
  borderWidth: 1,
  pageBreakAfter: "always",
}));

interface ZoneNightReportProps {
  path: string[];
  subjectIDs: string[];
}
export const ZoneNightReport = ({ path, subjectIDs }: ZoneNightReportProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const nightReportFilter = useMonitoringStore(
    (state) => state.nightReportFilter,
  );

  const { data: nightReportData, isLoading } =
    useGenerateReportsReportsGetQuery(
      {
        subjects: subjectIDs,
        reportDate: format(new Date(), "yyyy-MM-dd"),
      },
      {
        skip: subjectIDs.length === 0,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
      },
    );

  const nightReports =
    nightReportData?.data?.filter(
      (report) =>
        !nightReportFilter.showOnlyBadNights || hasDeviationAlert(report),
    ) ?? [];

  // On desktop the DataTable renders its own card, with the zone breadcrumb as
  // the top bar and the device count as the footer (same as the admin lists).
  if (!isSmall && nightReports.length > 0) {
    return (
      <Box sx={{ width: "100%", pageBreakAfter: "always" }}>
        <NightReportTable reports={nightReports} path={path} />
      </Box>
    );
  }

  // Mobile, loading, or empty: keep the bordered wrapper with the breadcrumb bar.
  return (
    <Wrapper>
      <Box
        sx={{
          height: 50,
          backgroundColor: "background.paper",
          paddingInline: 2.5,
          paddingBlock: 1,
          borderRadius: "8px 8px 0px 0px",
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <ZoneBreadcrumbs path={path} />
      </Box>
      <Box>
        {isLoading && (
          <LinearProgress variant={"indeterminate"} sx={{ marginBottom: 1 }} />
        )}
        {!isLoading && nightReports.length === 0 && (
          <Typography
            variant="bodyMicroRegular"
            color="textSecondary"
            sx={{ padding: 1.5 }}
          >
            {t("no_subjects_with_device_in_this_zone")}
          </Typography>
        )}
        {!isLoading && nightReports.length > 0 && isSmall && (
          <NightReportAccordion reports={nightReports} />
        )}
      </Box>
    </Wrapper>
  );
};
